// @flow

import React from "react";

import style from "./style.module.scss";
import { Link } from "gatsby";
import { GatsbyImage } from "gatsby-plugin-image";
import { t } from "ttag";

import { Icon, ChevronRight } from "@2po-c21/components";

export type RelatedAgency = {
    id: string,
    name: string,
    location: { longitude: string, latitude: string },
    address: {
        city: string,
        postalCode: string,
        number: string,
        street: string,
        countryCode: string,
    },
    images: Array<{
        description: string,
        name: string,
    }>,
};

type Props = {
    agency: {
        api_id: string,
        slug: string,
        name: string,
        phoneNumber: string,
        images: Array<{
            description: string,
            fixed: *,
        }>,
        email: string,
        location: { longitude: string, latitude: string },
        address: {
            city: string,
            postalCode: string,
            number: string,
            street: string,
            countryCode: string,
        },
    },
    fallbackImage: string,
    relatedAgencies: Array<*>,
    relatedAgenciesTitle?: string,
    locale?: string,
};

export const createAgencySlug = (name, id, locale) => {
    const formattedName = name
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/\s+/g, "-");
    return `/${locale}/${t`slug.agency`}/${formattedName}/${id}`;
};

export const AgencyMapper = ({
    agency,
    relatedAgencies,
    relatedAgenciesTitle,
    fallbackImage,
    locale,
}: Props) => {
    const image = agency.images[0] || fallbackImage;
    const safeRelatedAgencies = relatedAgencies || [];

    return (
        <div className={style.agencyList}>
            <div className={style.mainAgency}>
                <div
                    className={`${style.agencyCard} ${style.mainAgency}`}
                    key={agency?.api_id}
                >
                    <GatsbyImage
                        image={image?.gatsbyImageData}
                        alt={image?.description}
                        className={style.agencyImage}
                    />

                    <div className={style.agencyInformation}>
                        <div className={style.agencyName}>{agency?.name}</div>
                        <div className={style.agencyAddress}>
                            {agency.address.street} {agency.address.number},
                            <br />
                            {agency.address.postalCode} {agency.address.city}
                        </div>
                        <Link
                            to={createAgencySlug(
                                agency?.name,
                                agency?.api_id,
                                locale,
                            )}
                            className={style.agencyLink}
                        >
                            {t`agency.link.this-agency`}
                        </Link>
                    </div>
                </div>
            </div>
            <div className={style.relatedAgencies}>
                <div className={style.relatedAgenciesTitle}>
                    {relatedAgenciesTitle} ({safeRelatedAgencies?.length})
                </div>
                {safeRelatedAgencies &&
                    safeRelatedAgencies.map((relatedAgency) => {
                        return (
                            <div
                                className={style.agencyCard}
                                key={relatedAgency.id}
                            >
                                {relatedAgency?.images.length > 0 ? (
                                    <GatsbyImage
                                        image={
                                            relatedAgency?.images?.[0]
                                                ?.gatsbyImageData
                                        }
                                        alt={
                                            relatedAgency?.images?.[0]
                                                ?.description || ""
                                        }
                                        className={style.agencyImage}
                                    />
                                ) : (
                                    <GatsbyImage
                                        image={fallbackImage?.gatsbyImageData}
                                        alt={fallbackImage?.description}
                                        className={style.agencyImage}
                                    />
                                )}
                                <div className={style.agencyInformation}>
                                    <div className={style.agencyName}>
                                        {relatedAgency.name}
                                    </div>
                                    <div className={style.agencyAddress}>
                                        {relatedAgency.address.street}{" "}
                                        {relatedAgency.address.number},
                                        <br />
                                        {relatedAgency.address.postalCode}{" "}
                                        {relatedAgency.address.city}
                                    </div>
                                    <Link
                                        to={createAgencySlug(
                                            relatedAgency?.name,
                                            relatedAgency?.id,
                                            locale,
                                        )}
                                        className={style.agencyLink}
                                    >
                                        {t`agency.link.related-agencies`}
                                        <Icon
                                            icon={ChevronRight}
                                            size={"xs"}
                                            variant={"relentless-gold"}
                                        />
                                    </Link>
                                </div>
                            </div>
                        );
                    })}
            </div>
        </div>
    );
};

export default AgencyMapper;
